import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  Box, Typography, Grid, Card, CardContent, CardActions,
  Button, Chip, CircularProgress, Tooltip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControlLabel, Checkbox, Divider, LinearProgress
} from '@mui/material';
import {
  CloudUpload, Download, CheckCircle, Cancel,
  HourglassEmpty, UploadFile, Notifications
} from '@mui/icons-material';


const SIGNOFF_TYPES = [
  {
    key: 'SIT',
    label: 'System Integration Testing',
    color: '#1565c0',
    bg: '#e3f2fd',
    icon: '🔗',
  },
  {
    key: 'UAT',
    label: 'User Acceptance Testing',
    color: '#6a1b9a',
    bg: '#f3e5f5',
    icon: '👥',
  },
  {
    key: 'final',
    label: 'Final Sign-off',
    color: '#1b5e20',
    bg: '#e8f5e9',
    icon: '✅',
  },
];

const STATUS_CONFIG = {
  pending:  { label: 'Pending',  color: '#e65100', bg: '#fff3e0', icon: <HourglassEmpty fontSize="small" /> },
  approved: { label: 'Approved', color: '#2e7d32', bg: '#e8f5e9', icon: <CheckCircle fontSize="small" /> },
  rejected: { label: 'Rejected', color: '#c62828', bg: '#ffebee', icon: <Cancel fontSize="small" /> },
};

const baseURL = process.env.REACT_APP_API_URL || '';

export default function ProjectSignoff({ projectId }) {
  const [signoffs, setSignoffs] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [activeType, setActiveType] = useState(null);
  const [uploadForm, setUploadForm] = useState({ file: null, notes: '', notify_team: true });
  const [uploading, setUploading] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchSignoffs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseURL}/api/projects/${projectId}/signoffs`, { headers });
      const map = {};
      (res.data.Data || []).forEach(s => { map[s.type] = s; });
      setSignoffs(map);
    } catch (e) {
      Swal.fire('Error', 'Failed to load signoffs.', 'error');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (projectId) fetchSignoffs();
  }, [projectId, fetchSignoffs]);

  const openUpload = (type) => {
    setActiveType(type);
    setUploadForm({ file: null, notes: '', notify_team: true });
    setUploadOpen(true);
  };

  const handleUpload = async () => {
    if (!uploadForm.file) {
      Swal.fire('Validation', 'Please select a document to upload.', 'warning');
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append('type', activeType);
    fd.append('document', uploadForm.file);
    fd.append('notes', uploadForm.notes);
    fd.append('notify_team', uploadForm.notify_team ? '1' : '0');
    try {
      await axios.post(`${baseURL}/api/projects/${projectId}/signoffs`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      Swal.fire({ icon: 'success', title: `${activeType} Uploaded!`, text: 'Sign-off document uploaded successfully.', timer: 1800, showConfirmButton: false });
      setUploadOpen(false);
      fetchSignoffs();
    } catch (e) {
      Swal.fire('Error', 'Upload failed.', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleStatusUpdate = async (signoffId, status) => {
    try {
      await axios.put(`${baseURL}/api/projects/${projectId}/signoffs/${signoffId}`, { status }, { headers });
      Swal.fire({ icon: 'success', title: `Sign-off ${status}`, timer: 1500, showConfirmButton: false });
      fetchSignoffs();
    } catch (e) {
      Swal.fire('Error', 'Failed to update status.', 'error');
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight={700}>Project Sign-offs</Typography>
        <Typography variant="caption" color="text.secondary">
          Track SIT, UAT, and Final approval documents
        </Typography>
      </Box>

      {/* Overall progress */}
      <Box sx={{ mb: 3, p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="caption" fontWeight={700} color="text.secondary">COMPLETION</Typography>
          <Typography variant="caption" fontWeight={700} color="primary">
            {Object.values(signoffs).filter(s => s.status === 'approved').length} / 3 Approved
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={(Object.values(signoffs).filter(s => s.status === 'approved').length / 3) * 100}
          sx={{ height: 8, borderRadius: 4, bgcolor: '#e0e0e0',
            '& .MuiLinearProgress-bar': { borderRadius: 4, bgcolor: '#2e7d32' } }}
        />
      </Box>

      <Grid container spacing={2}>
        {SIGNOFF_TYPES.map((type) => {
          const signoff = signoffs[type.key];
          const status = signoff?.status || null;
          const statusCfg = status ? STATUS_CONFIG[status] : null;

          return (
            <Grid item xs={12} md={4} key={type.key}>
              <Card elevation={0} sx={{
                border: `2px solid ${signoff ? type.color + '40' : '#f0f0f0'}`,
                borderRadius: 3,
                height: '100%',
                transition: 'box-shadow 0.2s',
                '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.08)' },
              }}>
                <CardContent sx={{ pb: 1 }}>
                  {/* Header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Box sx={{
                      width: 42, height: 42, bgcolor: type.bg,
                      borderRadius: 2, display: 'flex', alignItems: 'center',
                      justifyContent: 'center', fontSize: 20
                    }}>
                      {type.icon}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight={700} sx={{ color: type.color }}>
                        {type.key}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{type.label}</Typography>
                    </Box>
                    {statusCfg && (
                      <Chip
                        icon={statusCfg.icon}
                        label={statusCfg.label}
                        size="small"
                        sx={{
                          bgcolor: statusCfg.bg,
                          color: statusCfg.color,
                          fontSize: 11,
                          fontWeight: 700,
                          '& .MuiChip-icon': { color: statusCfg.color }
                        }}
                      />
                    )}
                  </Box>

                  {signoff ? (
                    <Box>
                      {signoff.notes && (
                        <Box sx={{ bgcolor: '#f8f9fa', borderRadius: 1, p: 1, mb: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            📝 {signoff.notes}
                          </Typography>
                        </Box>
                      )}
                      <Typography variant="caption" color="text.secondary" display="block">
                        Uploaded: {new Date(signoff.created_at).toLocaleDateString()}
                      </Typography>
                      {signoff.uploaded_by && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          By: {signoff.uploaded_by}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ py: 2, textAlign: 'center', color: '#bbb' }}>
                      <UploadFile sx={{ fontSize: 32, mb: 0.5 }} />
                      <Typography variant="caption" display="block">No document uploaded</Typography>
                    </Box>
                  )}
                </CardContent>

                <Divider />
                <CardActions sx={{ px: 2, py: 1, gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    startIcon={<CloudUpload />}
                    onClick={() => openUpload(type.key)}
                    sx={{ textTransform: 'none', fontSize: 12, fontWeight: 600 }}
                  >
                    {signoff ? 'Re-upload' : 'Upload'}
                  </Button>

                  {signoff?.document_url && (
                    <Button
                      size="small"
                      startIcon={<Download />}
                      component="a"
                      href={signoff.document_url}
                      target="_blank"
                      sx={{ textTransform: 'none', fontSize: 12 }}
                    >
                      View
                    </Button>
                  )}

                  {signoff && status === 'pending' && (
                    <>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircle />}
                        onClick={() => handleStatusUpdate(signoff.id, 'approved')}
                        sx={{ textTransform: 'none', fontSize: 11, ml: 'auto' }}
                      >
                        Approve
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<Cancel />}
                        onClick={() => handleStatusUpdate(signoff.id, 'rejected')}
                        sx={{ textTransform: 'none', fontSize: 11 }}
                      >
                        Reject
                      </Button>
                    </>
                  )}
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Upload {activeType} Sign-off Document
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              sx={{
                border: '2px dashed #1a73e8', borderRadius: 2, p: 3,
                textAlign: 'center', cursor: 'pointer', bgcolor: '#f0f4ff',
                '&:hover': { bgcolor: '#e3ecff' }
              }}
              onClick={() => document.getElementById('signoff-file-input').click()}
            >
              <input
                id="signoff-file-input"
                type="file"
                hidden
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={e => setUploadForm({ ...uploadForm, file: e.target.files[0] })}
              />
              <CloudUpload sx={{ fontSize: 36, color: '#1a73e8', mb: 1 }} />
              <Typography variant="body2" fontWeight={600} color="primary">
                {uploadForm.file ? uploadForm.file.name : 'Click to select file'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                PDF, DOC, DOCX, JPG, PNG (max 10MB)
              </Typography>
            </Box>

            <TextField
              label="Notes (optional)"
              multiline rows={2} fullWidth size="small"
              value={uploadForm.notes}
              onChange={e => setUploadForm({ ...uploadForm, notes: e.target.value })}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={uploadForm.notify_team}
                  onChange={e => setUploadForm({ ...uploadForm, notify_team: e.target.checked })}
                  size="small"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Notifications fontSize="small" color="primary" />
                  <Typography variant="body2">Notify team members via email</Typography>
                </Box>
              }
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setUploadOpen(false)} variant="outlined" size="small" sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            size="small"
            disabled={uploading || !uploadForm.file}
            startIcon={uploading ? <CircularProgress size={14} /> : <CloudUpload />}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {uploading ? 'Uploading...' : 'Upload Document'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
